import date from 'date-and-time';
import type { IntlShape } from 'react-intl';
import type { PresaveFansSummary } from 'fym-shared/src/smartlinks/presaveSubscriptions';

/** One bar pair of the growth chart: one day, or one week on a long period. */
export interface FansGrowthBucket {
  /** The first day of the bucket, YYYY-MM-DD (UTC). */
  start: string;
  subscribed: number;
  unsubscribed: number;
}

/** Past this many days the chart draws one bar pair per week, so the bars stay readable. */
export const FANS_DAILY_BARS_MAX_DAYS = 90;

function dayKey(day: Date): string {
  return date.format(day, 'YYYY-MM-DD', true);
}

/**
 * The bar pairs of the growth chart for the last `days` days up to `today` (UTC). A day that the
 * series does not hold counts 0. A period of more than 90 days goes in weeks.
 */
export function fansGrowthBuckets(series: PresaveFansSummary['series'], days: number, today: Date): FansGrowthBucket[] {
  const byDay = new Map(series.map((point) => [point.date, point]));
  const size = days > FANS_DAILY_BARS_MAX_DAYS ? 7 : 1;
  const first = date.addDays(today, -(days - 1));
  const buckets: FansGrowthBucket[] = [];

  for (let offset = 0; offset < days; offset += size) {
    const bucket: FansGrowthBucket = { start: dayKey(date.addDays(first, offset)), subscribed: 0, unsubscribed: 0 };
    for (let index = offset; index < Math.min(offset + size, days); index += 1) {
      const point = byDay.get(dayKey(date.addDays(first, index)));
      bucket.subscribed += point?.subscribed ?? 0;
      bucket.unsubscribed += point?.unsubscribed ?? 0;
    }
    buckets.push(bucket);
  }
  return buckets;
}

/** The indexes of up to five evenly spaced axis labels: the first and the last bucket included. */
export function fansAxisIndexes(count: number): number[] {
  if (count <= 1) {
    return count === 1 ? [0] : [];
  }
  const labels = Math.min(5, count);
  return [...new Set(Array.from({ length: labels }, (_, i) => Math.round((i * (count - 1)) / (labels - 1))))];
}

/** The short day of a bucket ("Oct 4"), in UTC like the series. */
export function formatBucketDay(intl: IntlShape, start: string): string {
  return intl.formatDate(`${start}T00:00:00Z`, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}
